import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { AxiosError } from 'axios';

import api from '../../../api/axios';
import useGetUIErrorMessage from '../../../hooks/useGetUIErrorMessage';
import { IApiState } from '../../../types/api';
import { RootState } from '../..';
import { showAlert } from '../appToast/appToastSlice';
import { ITransaction2faApiResponse } from './transaction2faTypes';

interface IVerifyTransaction2faTokenInput {
  token: string;
}

const initialState: IApiState<ITransaction2faApiResponse> = {
  value: null,
  status: 'idle',
  error: '',
};

export const verifyTransaction2faTokenAsync = createAsyncThunk(
  'transaction/verify-2fa-token',
  async (
    { token }: IVerifyTransaction2faTokenInput,
    { dispatch, getState, rejectWithValue },
  ) => {
    try {
      const state = getState() as RootState;
      const accessToken = state.auth.value?.data?.token?.accessToken;
      const businessId =
        localStorage.getItem('businessId') ||
        state.auth.value?.data?.user?.businesses?.[0]?.id;

      const { data } = await api.post<ITransaction2faApiResponse>(
        '/business/transaction/verify-2fa-token',
        { token },
        {
          headers: {
            Authorization: `Token ${accessToken}`,
            'X-Business-Id': businessId,
          },
        },
      );

      return data;
    } catch (err) {
      const error = err as AxiosError<ITransaction2faApiResponse>;

      const errorMessage = useGetUIErrorMessage({
        message: error.response?.data?.message,
        errors: error.response?.data?.errors,
      });

      dispatch(showAlert({ status: 'error', description: errorMessage }));

      return rejectWithValue(error.response?.data);
    }
  },
);

export const verifyTransaction2faTokenSlice = createSlice({
  name: 'transaction/verify-2fa-token',
  initialState,
  reducers: {
    resetVerifyTransaction2faToken: (state) => {
      state.value = null;
      state.status = 'idle';
      state.error = '';
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(verifyTransaction2faTokenAsync.pending, (state) => {
        state.status = 'loading';
        state.error = '';
      })
      .addCase(verifyTransaction2faTokenAsync.fulfilled, (state, action) => {
        state.status = 'success';
        state.value = action.payload;
      })
      .addCase(verifyTransaction2faTokenAsync.rejected, (state, action) => {
        state.status = 'failed';
        state.value = action.payload as ITransaction2faApiResponse;
        state.error = action.error.message;
      });
  },
});

export const { resetVerifyTransaction2faToken } =
  verifyTransaction2faTokenSlice.actions;

export default verifyTransaction2faTokenSlice.reducer;
